import { useEffect, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, Activity, AlertTriangle,
  Clock, CheckCircle2, Building2, Banknote,
} from "lucide-react";

function Kpi({ label, value, sub, accent, icon: Icon, testid }) {
  return (
    <Card className="rounded-sm border border-zinc-200 shadow-none p-5 hover:bg-zinc-50 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />}
      </div>
      <div className={`font-display text-3xl font-bold tabular ${accent || "text-zinc-900"}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1 tabular">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const [k, setK] = useState(null);

  useEffect(() => {
    api.get("/dashboard/kpis").then(({ data }) => setK(data));
  }, []);

  if (!k) {
    return <div className="text-sm text-zinc-500" data-testid="dashboard-loading">Carregando indicadores…</div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Visão geral</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">Indicadores operacionais e financeiros do tenant.</p>
        </div>
        <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest">
          {k.bancos_configurados} bancos ativos
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Boletos emitidos hoje" value={k.emitidos_hoje} icon={Activity} testid="kpi-emitidos-hoje" />
        <Kpi label="Boletos pagos hoje" value={k.pagos_hoje} icon={CheckCircle2} accent="text-emerald-600" testid="kpi-pagos-hoje" />
        <Kpi label="Recebido hoje" value={formatBRL(k.valor_recebido_hoje)} icon={Banknote} accent="text-emerald-600" testid="kpi-valor-hoje" />
        <Kpi label="PIX recebidos (total)" value={k.pix_recebidos} sub={formatBRL(k.pix_recebidos_valor)} icon={TrendingUp} testid="kpi-pix" />
        <Kpi label="Em aberto" value={k.em_aberto} sub={formatBRL(k.valor_em_aberto)} icon={Clock} testid="kpi-aberto" />
        <Kpi label="Vencidos" value={k.vencidos} sub={formatBRL(k.valor_vencido)} icon={AlertTriangle} accent="text-red-600" testid="kpi-vencidos" />
        <Kpi label="Inadimplência" value={`${k.inadimplencia}%`} icon={TrendingDown} accent={k.inadimplencia > 10 ? "text-red-600" : "text-zinc-900"} testid="kpi-inadimplencia" />
        <Kpi label="Total recebido" value={formatBRL(k.total_pagos_valor)} icon={Building2} testid="kpi-total-recebido" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 rounded-sm border border-zinc-200 shadow-none p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Últimos 7 dias</div>
              <h3 className="font-display text-lg font-semibold">Recebimentos diários</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={k.serie_7d} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#e4e4e7" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="data" stroke="#a1a1aa" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <YAxis stroke="#a1a1aa" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <Tooltip
                  contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }}
                  formatter={(v) => formatBRL(v)}
                />
                <Line type="monotone" dataKey="valor" stroke="#F26522" strokeWidth={2.5} dot={{ r: 4, fill: "#F26522", strokeWidth: 0 }} activeDot={{ r: 6, fill: "#F26522" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-sm border border-zinc-200 shadow-none p-5">
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Distribuição</div>
            <h3 className="font-display text-lg font-semibold">Pix por banco</h3>
          </div>
          {(k.pix_por_banco || []).length === 0 ? (
            <div className="text-sm text-zinc-500 py-12 text-center" data-testid="bancos-empty">
              Nenhum Pix recebido ainda.
            </div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={k.pix_por_banco || []} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e4e7" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="banco" stroke="#a1a1aa" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <YAxis stroke="#a1a1aa" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                  <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} formatter={(v) => formatBRL(v)} />
                  <Bar dataKey="valor" fill="#F26522" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
